import { c as _c } from "react/compiler-runtime";
import { useLazyQuery } from '@apollo/client/react';
import { Box, Button, Center, ProgressCircle, Collapsible, Flex, Heading, HStack, Icon, IconButton, Link, Spinner, Stack, Text, useDisclosure, VStack, Wrap, Presence, AbsoluteCenter, chakra } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { useMatchingContext } from '@src/components/sco/Matching/Layout/MatchingContext';
import { UserMatchExpandedProfile } from '@src/components/sco/Matching/UserMatchExpandedProfile';
import { UserMatchExplanation } from '@src/components/sco/Matching/UserMatchExplanation';
import { UserMatchRatingButtons } from '@src/components/sco/Matching/UserMatchRatingButtons';
import { UserMatchSaveButton } from '@src/components/sco/Matching/UserMatchSaveButton';
import { UserMatchSendMessageButton } from '@src/components/sco/Matching/UserMatchSendMessageButton';
import { USER_MATCH_FRAGMENT } from '@src/components/sco/Matching/userSearchFragment';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import useOnScreen from '@src/hooks/useOnScreen';
import { useUserAccessCheck } from '@src/hooks/useUserAccessCheck';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import { FaBriefcase, FaChevronDown, FaChevronUp, FaLinkedin, FaMapMarkerAlt } from 'react-icons/fa';
export const UserMatchCard = (props: {
  delayLoading?: boolean;
  userMatchId: string;
  showUserSearchBanner?: boolean;
  isExpanded?: boolean;
  trackView?: boolean;
}) => {
  const matchingContext = useMatchingContext();
  const profileExpansion = useDisclosure({
    open: props.isExpanded
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(cardRef);
  const isOnScreenDelayed = useDebounce(isOnScreen, 200);
  const matchingAccessCheck = useUserAccessCheck({
    require: {
      contentPermissions: ['canAccessMemberMatching']
    }
  });
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMobile = getBreakpointValue([true, true, false, false]);
  const [runQuery, query] = useLazyQuery(gql`
    query($userMatchId: String!) {
      scoUserMatch(userMatchId: $userMatchId) {
        id
        ${USER_MATCH_FRAGMENT}
        ${props.showUserSearchBanner ? `
              userSearch {
                id
                title
                userSearchId
              }
            ` : ''}

      }
    }
  `);
  const userMatch = query.data?.scoUserMatch;
  const targetUser = userMatch?.targetUser;
  useEffect(() => {
    if (!props.delayLoading) {
      runQuery({
        variables: {
          userMatchId: props.userMatchId
        }
      });
    }
  }, [props.delayLoading, props.userMatchId]);
  useEffect(() => {
    // Close expanded profile when this becomes inactive in the carousel
    // This fixes issue with carousel heights and scrolling to blank space
    if (userMatch?.userMatchId && profileExpansion.open && matchingContext.activeUserMatchId && matchingContext.activeUserMatchId !== userMatch?.userMatchId) {
      profileExpansion.onClose();
    }
  }, [userMatch?.userMatchId, matchingContext.activeUserMatchId]);
  if (!userMatch) {
    if (query.error) {
      return <Center padding={'1em'} width={'full'}>
          <Text>
            {'Error: '}
            {query.error.toString()}
          </Text>
        </Center>;
    }
    return <Center padding={'1em'} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (props.delayLoading) {
    return <></>;
  }
  const targetUserDisplayName = matchingAccessCheck?.accessCheckPassed ? targetUser.fullName : [targetUser.firstName, targetUser.lastName ? targetUser.lastName[0] + '.' : null].filter(Boolean).join(' ');
  return <Box ref={cardRef} bgColor={'colorPalette.container'} borderColor={'colorPalette.border'} borderRadius={getBreakpointValue(['xl', 'xl', '3xl', '3xl'])} borderTopWidth={'1px'} boxShadow={'md'} margin={'auto'} // IMPORTANT: this fixes scrolling inside carousel
  maxWidth={'container.lg'} width={'full'}>
      {props.trackView && userMatch && <ViewableTracker trackScrolling={true} viewableId={userMatch.userMatchId} viewableType={'UserMatch'} />}
      {props.showUserSearchBanner && userMatch.userSearch && <Stack align={getBreakpointValue(['start', 'start', 'center'])} bgColor={'colorPalette.container'} borderTopRadius={getBreakpointValue(['xl', 'xl', '3xl', '3xl'])} direction={getBreakpointValue(['row', 'row', 'row'])} justify={getBreakpointValue(['space-between', 'center', 'center'])} padding={2} textAlign={getBreakpointValue(['start', 'center', 'center'])}>
          <Text color={'white'} fontSize={'sm'}>
            {'Matched on '}
            <strong>"{userMatch.userSearch.title}"</strong>
          </Text>
          <Link as={NextLink} fontSize={isMobile ? '0.713rem' : '0.813rem'} fontWeight={'600'} href={`/members/matching/${userMatch.userSearch.userSearchId}`} variant={'underline'}>
            {isMobile ? 'View All' : 'View All Matches'}
          </Link>
        </Stack>}
      <Stack align={getBreakpointValue(['center', 'center', 'center', 'stretch'])} direction={getBreakpointValue(['column', 'column', 'column', 'row'])} gap={getBreakpointValue(['4', '4', '10', '10'])} paddingX={getBreakpointValue(['2', '4', '8', '8'])} paddingY={getBreakpointValue(['2', '4', '8', '8'])}>
        <VStack align={'stretch'} flex={2} gap={getBreakpointValue([2, 4, 8, 8])} justify={'flex-start'} padding={0}>
          <Stack align={getBreakpointValue(['flex-start', 'flex-start', 'center'])} direction={getBreakpointValue(['row', 'row', 'row'])} gap={5}>
            <Box display={getBreakpointValue(['none', 'none', 'block'])}>
              <UserAvatar user={targetUser} avatarProps={{
              height: getBreakpointValue(['40px', '60px', '140px']),
              width: getBreakpointValue(['40px', '60px', '140px'])
            }} />
            </Box>
            <VStack align={getBreakpointValue(['center', 'center', 'flex-start'])} bgColor={getBreakpointValue(['primaryAlpha.200', 'colorPalette.bg', 'transparent'])} borderRadius={getBreakpointValue(['xl', 'xl', 'none'])} gap={2} padding={getBreakpointValue([4, 4, 0])} width={'full'}>
              <HStack>
                <Box display={getBreakpointValue(['block', 'block', 'none'])}>
                  <UserAvatar user={targetUser} avatarProps={{
                  height: '1.25em',
                  width: '1.25em'
                }} />
                </Box>
                <Heading size={getBreakpointValue(['lg', '2xl', '3xl'])}>{targetUserDisplayName}</Heading>
              </HStack>
              {matchingAccessCheck.accessCheckPassed && <>
                  <Wrap gap={2}>
                    {targetUser.company.name && <HStack align={'center'} gap={1} justify={'center'}>
                        <Icon as={FaBriefcase} color={'colorPalette.fg/50'} />

                        <Text color={'colorPalette.fg/50'} fontWeight={'semibold'} textStyle={'sm'}>
                          {targetUser.company.name}
                        </Text>
                      </HStack>}
                    {targetUser.location && <HStack align={'center'} gap={1} justify={'center'}>
                        <Icon as={FaMapMarkerAlt} color={'colorPalette.fg/50'} />
                        <Text color={'colorPalette.fg/50'} fontWeight={'semibold'} textStyle={'sm'}>
                          {targetUser.location}
                        </Text>
                      </HStack>}
                  </Wrap>
                </>}

              <Wrap gap={2} marginTop={2}>
                <Flex>
                  <PaywalledAction contentPermission={'canAccessMemberMatching'} upgradeBoxProps={{
                  width: 'auto'
                }} upgradeCtaButtonProps={{
                  size: 'sm'
                }} upgradeCtaLabel={'Upgrade to Message'} variant={'button-only'}>
                    <UserMatchSendMessageButton userMatchId={userMatch.userMatchId} />
                  </PaywalledAction>
                </Flex>
                <UserMatchSaveButton userMatchId={userMatch.userMatchId} />
                {matchingAccessCheck.accessCheckPassed && targetUser.linkedin && <IconButton
              // as={NextLink}
              aria-label={'LinkedIn'} variant={'outline'} asChild
              // target={'_blank'}
              size={getBreakpointValue(['xs', 'xs', 'sm'])}>
                    <NextLink href={targetUser.linkedin} target={'_blank'}>
                      <Icon as={FaLinkedin} />
                    </NextLink>
                  </IconButton>}
              </Wrap>
            </VStack>
          </Stack>
          <Box width={'full'} display={getBreakpointValue(['block', 'block', 'none'])}
        // marginBottom={getBreakpointValue([2, 4, 0])}
        >
            <ProfileCardMatchScore isOnScreen={isOnScreen} isOnScreenDelayed={isOnScreenDelayed} matchFactors={userMatch.matchFactors} matchScoreAsPercent={userMatch.matchScoreAsPercent} matchScoreAsPoints={userMatch.matchScore} userMatch={userMatch} />
          </Box>
          <VStack align={'flex-start'} bgColor={getBreakpointValue(['backgroundSecondary', 'backgroundSecondary', 'transparent'])} borderRadius={getBreakpointValue(['xl', 'xl', 'none'])} gap={0} padding={getBreakpointValue([4, 4, 0])} width={'full'}>
            <Heading color={'colorPalette.fg/50'} fontSize={'xs'} textStyle={'label'}>
              Why you should meet:
            </Heading>
            <UserMatchExplanation effect={'collapse'} preloadedUserMatchExplanation={userMatch.matchExplanation} userMatchId={userMatch.userMatchId} />
          </VStack>
        </VStack>
        <Box display={getBreakpointValue(['none', 'none', 'block'])} width={getBreakpointValue(['full', 'full', 'full', 'auto'])}>
          <ProfileCardMatchScore isOnScreen={isOnScreen} isOnScreenDelayed={isOnScreenDelayed} matchFactors={userMatch.matchFactors} matchScoreAsPercent={userMatch.matchScoreAsPercent} matchScoreAsPoints={userMatch.matchScore} userMatch={userMatch} />
        </Box>
      </Stack>
      <Box bgColor={'colorPalette.bg/50'} borderBottomRadius={getBreakpointValue(['xl', 'xl', '3xl', '3xl'])} display={getBreakpointValue(['none', 'none', 'block'])} paddingX={2} paddingY={2}>
        <Button size={'sm'} variant={'ghost'} width={'full'} onClick={profileExpansion.onToggle}>
          {profileExpansion.open ? 'Close Profile' : 'Expand Profile'}
          <Icon as={profileExpansion.open ? FaChevronUp : FaChevronDown} />
        </Button>
        <Collapsible.Root open={profileExpansion.open}>
          <Collapsible.Content>
            {matchingAccessCheck.accessCheckPassed ? <UserMatchExpandedProfile userMatchId={props.userMatchId} /> : <PaywalledAction contentPermission={'canAccessMemberMatching'} title={`Upgrade to view ${targetUser?.firstName || 'user'}'s full profile.`} variant={'boxed'} />}
          </Collapsible.Content>
        </Collapsible.Root>
      </Box>
    </Box>;
};
export const ProfileCardMatchScore = props => {
  const $ = _c(49);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue([true, true, false, false]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isMobile = t0;
  let t1;
  if ($[2] !== getBreakpointValue) {
    t1 = getBreakpointValue(["xl", "xl", "2xl"]);
    $[2] = getBreakpointValue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== getBreakpointValue) {
    t2 = getBreakpointValue(["row", "row", "column"]);
    $[4] = getBreakpointValue;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== getBreakpointValue) {
    t3 = getBreakpointValue([2, 4, 4]);
    $[6] = getBreakpointValue;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props.isOnScreen || $[9] !== props.matchScoreAsPercent || $[10] !== props.matchScoreAsPoints) {
    t4 = <ProfileCardAnimatedMatchScore in={props.isOnScreen} matchScoreAsPercent={props.matchScoreAsPercent} matchScoreAsPoints={props.matchScoreAsPoints} />;
    $[8] = props.isOnScreen;
    $[9] = props.matchScoreAsPercent;
    $[10] = props.matchScoreAsPoints;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <VStack align="center" padding={t3} width="full">{t4}</VStack>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      _light: "colorPalette.card",
      _dark: "colorPalette.solid"
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== getBreakpointValue) {
    t7 = getBreakpointValue(["none", "none", "2xl"]);
    $[16] = getBreakpointValue;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== getBreakpointValue) {
    t8 = getBreakpointValue([2, 4, 4]);
    $[18] = getBreakpointValue;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== props.isOnScreen || $[21] !== props.isOnScreenDelayed || $[22] !== props.matchFactors) {
    t9 = props.matchFactors?.map((t10, index) => {
      const [points,, title] = t10;
      return <Presence key={index} animationDuration="moderate" present={props.isOnScreenDelayed} animationStyle={{
        _open: "scale-fade-in",
        _closed: "scale-fade-out"
      }}><Flex align="center" bgColor="colorPalette.bg/20" borderRadius="md" justify="center" padding={2}><Text key={index} animation={props.isOnScreen ? "2s ease-in-out bgOpacity" : "none"} color="colorPalette.fg" fontWeight="semibold" textAlign="center" textStyle="xs" css={{
            "--bg-opacity-from": "var(--chakra-colors-whiteAlpha-200)",
            "--bg-opacity-to": "var(--chakra-colors-whiteAlpha-400)"
          }}>{title}{" "}<chakra.span backgroundColor="colorPalette.fg/20" borderRadius="md" fontSize="xs" marginLeft="0.5em" paddingX="0.5em" paddingY="0.25em">+{points}pts</chakra.span></Text></Flex></Presence>;
    });
    $[20] = props.isOnScreen;
    $[21] = props.isOnScreenDelayed;
    $[22] = props.matchFactors;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <VStack align="stretch" padding={t8} width="full">{t9}</VStack>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== getBreakpointValue) {
    t11 = getBreakpointValue(["column", "row"]);
    $[27] = getBreakpointValue;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== getBreakpointValue) {
    t12 = getBreakpointValue([2, 4, 4]);
    $[29] = getBreakpointValue;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== isMobile) {
    t13 = !isMobile && <HStack align="center" justify="center" width="full"><Text fontSize="2xs" textStyle="label" whiteSpace="nowrap">Rate this Match</Text></HStack>;
    $[31] = isMobile;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== props.userMatch) {
    t14 = <UserMatchRatingButtons userMatch={props.userMatch} />;
    $[33] = props.userMatch;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t14) {
    t15 = <Stack align="stretch" bgColor="colorPalette.bg/20" direction={t11} padding={t12} width="full">{t13}{t14}</Stack>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t10 || $[41] !== t15 || $[42] !== t7) {
    t16 = <Stack align="center" alignSelf="stretch" bgColor={t6} borderTopRadius={t7} boxShadow="sm" flexWrap="wrap" gap={0} justify="space-between" padding={0} w="100%">{t10}{t15}</Stack>;
    $[40] = t10;
    $[41] = t15;
    $[42] = t7;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t1 || $[45] !== t16 || $[46] !== t2 || $[47] !== t5) {
    t17 = <Stack borderRadius={t1} boxShadow="sm" direction={t2} flex={1} gap={0} overflow="hidden" width="full" align="center" bgColor="colorPalette.bg">{t5}{t16}</Stack>;
    $[44] = t1;
    $[45] = t16;
    $[46] = t2;
    $[47] = t5;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
};
export const ProfileCardAnimatedMatchScore = props => {
  const $ = _c(32);
  const onScreenRef = useRef(null);
  const debouncedShowScoreFirst = useDebounce(props.in, 100);
  const debouncedShowScoreSecond = useDebounce(props.in, 150);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue(["xl", "xl", "3xl", "7xl"]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== debouncedShowScoreSecond || $[3] !== props.matchScoreAsPercent) {
    t1 = debouncedShowScoreSecond ? Math.round(props.matchScoreAsPercent || 1) : 1;
    $[2] = debouncedShowScoreSecond;
    $[3] = props.matchScoreAsPercent;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      "& circle": {
        transition: "1.5s cubic-bezier(0.33, 1, 0.68, 1)"
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ProgressCircle.Circle><ProgressCircle.Track /><ProgressCircle.Range stroke="colorPalette.border" strokeLinecap="round" /></ProgressCircle.Circle>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== getBreakpointValue) {
    t4 = getBreakpointValue(["none", "none", "block", "block"]);
    $[7] = getBreakpointValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <Text color="colorPalette.border" display={t4} textStyle="label">Score</Text>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== getBreakpointValue) {
    t6 = getBreakpointValue(["xl", "xl", "2xl", "2xl"]);
    $[11] = getBreakpointValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== debouncedShowScoreFirst || $[14] !== props.matchScoreAsPercent) {
    t7 = debouncedShowScoreFirst ? <CountUp duration={1.5} end={Math.round(props.matchScoreAsPercent)} start={0} easingFn={_temp} /> : "0";
    $[13] = debouncedShowScoreFirst;
    $[14] = props.matchScoreAsPercent;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !props.showNumberOnly && "%";
  let t9;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = <Heading lineHeight="100%" size={t6}>{t7}{t8}</Heading>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== getBreakpointValue || $[21] !== props.matchScoreAsPoints || $[22] !== props.showNumberOnly) {
    t10 = !props.showNumberOnly && <Text color="colorPalette.fg/50" display={getBreakpointValue(["none", "none", "block", "block"])} fontSize="xs" fontWeight="semibold" textAlign="center">+{props.matchScoreAsPoints}pts</Text>;
    $[20] = getBreakpointValue;
    $[21] = props.matchScoreAsPoints;
    $[22] = props.showNumberOnly;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t5 || $[26] !== t9) {
    t11 = <AbsoluteCenter><VStack gap={1}>{t5}{t9}{t10}</VStack></AbsoluteCenter>;
    $[24] = t10;
    $[25] = t5;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t0 || $[29] !== t1 || $[30] !== t11) {
    t12 = <Box ref={onScreenRef}><ProgressCircle.Root colorPalette="teal" size={t0} value={t1} css={t2}>{t3}{t11}</ProgressCircle.Root></Box>;
    $[28] = t0;
    $[29] = t1;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
function _temp(t, b, c, d) {
  return c * ((t = t / d - 1) * t * t + 1) + b;
}